import { Loader2, Mail } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { RoleType } from "@/types/project";
import {
  usePendingOrganizationInvitationsQuery,
  useAcceptOrganizationInvitationMutation,
  useDeclineOrganizationInvitationMutation,
} from "@/hooks/queries/useOrganizationUsersQuery";

const getRoleLabel = (t: (key: string) => string, roleType: RoleType) => {
  switch (roleType) {
    case RoleType.Owner: return t("roleOwner");
    case RoleType.Editor: return t("roleEditor");
    case RoleType.Viewer: return t("projectViewer");
    default: return String(roleType);
  }
};

const PendingInvitationsBanner = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { data: invitations = [] } = usePendingOrganizationInvitationsQuery();
  const acceptMutation = useAcceptOrganizationInvitationMutation();
  const declineMutation = useDeclineOrganizationInvitationMutation();

  if (invitations.length === 0) return null;

  return (
    <Card className="mb-6 border-primary/30 bg-primary/5">
      <CardContent className="py-4">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={18} className="text-primary" />
          <h2 className="text-sm font-semibold text-foreground">{t("pendingInvitationsTitle")}</h2>
        </div>
        <div className="space-y-2">
          {invitations.map((invite) => {
            const isMutating =
              (acceptMutation.isPending && acceptMutation.variables?.organizationId === invite.organizationId) ||
              (declineMutation.isPending && declineMutation.variables?.organizationId === invite.organizationId);
            return (
              <div
                key={invite.organizationId}
                className="flex flex-col gap-2 rounded-lg border bg-background p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <p className="text-sm">
                  <span className="font-medium">{invite.invitedBy}</span> {t("invitedToJoin")}{" "}
                  <span className="font-medium">{invite.organizationName}</span>{" "}
                  <span className="text-muted-foreground">({getRoleLabel(t, invite.roleType)})</span>
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isMutating}
                    onClick={() => declineMutation.mutate({ organizationId: invite.organizationId })}
                  >
                    {declineMutation.isPending && declineMutation.variables?.organizationId === invite.organizationId && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    {t("declineInvitation")}
                  </Button>
                  <Button
                    size="sm"
                    disabled={isMutating}
                    onClick={() => acceptMutation.mutate({ organizationId: invite.organizationId })}
                  >
                    {acceptMutation.isPending && acceptMutation.variables?.organizationId === invite.organizationId && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    {t("acceptInvitation")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PendingInvitationsBanner;
